import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import {
  Step,
  StepsComponent,
} from '../../../shared/components/steps/steps.component';
import { CardCollapseComponent } from '../../../shared/components/collapse/card-collapse.component';
import { ConfirmacaoVagaService } from '../../../shared/services/confirmacao-vaga.service';
import { TermoEtapaCicloService } from '../../../shared/services/termo-etapa-ciclo.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { AlertService } from '../../../shared/services/alert.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Ciclo } from '../../../shared/models/response/ciclo.model';
import { UsuarioResponseDto } from '../../../shared/models/response/usuario-response.model';
import { Municipio, Uf } from '../../../shared/models/response/localidade.model';
import { InstalacaoMinimaModalComponent } from './instalacao-minima-modal.component';
import { CnesVaga, CnesVagaModalComponent } from './cnes-vaga-modal.component';
import { EtapaEnum } from '../../../core/enums/etapa.enum';
import { FaseEnum } from '../../../core/enums/fase.enum';
import { UploadService } from '../../../shared/services/upload.service';
import { UploadComponent } from '../../../shared/components/upload/upload.component';
import { Anexo } from '../../../shared/models/anexo.model';
import { ChamadaEtapaPerfilService } from '../../../shared/services/chamada-etapa-perfil.service';
import { ChamadaEtapaPerfil } from '../../../shared/models/response/chamada-etapa-perfil.model';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DateUtil } from '../../../shared/utils/date.util';
import { FileUtils } from '../../../shared/utils/file-utils';
import { HistoricoConfirmacaoVagaComponent } from '../historico/historico-confirmacao-vaga.component';

export interface VagaAprimoramentoGrupo {
  tipoAprimoramento: { id: number; nome: string; dsInstalacaoMinima: string };
  vagas: { id: number; vagaPrograma: any }[];
}

@Component({
  selector: 'app-confirmacao-vaga-aprimoramento',
  templateUrl: './confirmacao-vaga-aprimoramento.component.html',
  styleUrl: './confirmacao-vaga-aprimoramento.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    StepsComponent,
    CardCollapseComponent,
    UploadComponent,
  ],
})
export class ConfirmacaoVagaAprimoramentoComponent implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly alertService = inject(AlertService);
  private readonly loadingService = inject(LoadingService);
  private readonly confirmacaoVagaService = inject(ConfirmacaoVagaService);
  private readonly termoEtapaCicloService = inject(TermoEtapaCicloService);
  private readonly modalService = inject(NgbModal);
  private readonly uploadService = inject(UploadService);
  private readonly chamadaEtapaPerfilService = inject(ChamadaEtapaPerfilService);
  private readonly sanitizer = inject(DomSanitizer);

  @Input() cicloSelected: Ciclo | null = null;
  @Input() usuario!: UsuarioResponseDto;
  @Input() municipio: Municipio | null = null;
  @Input() uf: Uf | null = null;

  stepChangeEmitter = new EventEmitter<number>();

  currentStep = 1;
  submitting = false;
  confirmed = false;
  confirmedSemVagas = false;
  prazoEncerrado = false;
  chamadaCarregada = false;
  chamadaConfirmacao: ChamadaEtapaPerfil | null = null;

  vagasAprimoramento: VagaAprimoramentoGrupo[] = [];
  vagasCarregadas = false;
  termos: any[] = [];
  aceites = new Map<number, boolean>();
  instalacaoVista = new Set<number>();
  estabelecimentos = new Map<number, CnesVaga[]>();
  arquivosPorTermo = new Map<number, File>();
  ciencias = new Map<number, boolean>();
  nuRefDocumentoConfirmacao: string | null = null;
  dataConfirmacao: Date | null = null;

  steps: Step[] = [
    { number: 1, title: 'Dados da Adesão', active: true, completed: false, disabled: false },
    { number: 2, title: 'Resumo', active: false, completed: false, disabled: true },
    { number: 3, title: 'Termos de Adesão', active: false, completed: false, disabled: true },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cicloSelected']) {
      if (this.cicloSelected) {
        this.resetState();
        this.checkConfirmacaoExistente(this.cicloSelected.id);
      } else {
        this.resetState();
      }
    }
  }

  onClickStep(step: any): void {
    if (step.disabled) return;
    //this.goToStep(step.number);
  }

  onAceiteChange(tipoId: number, event: Event): void {
    if (!this.instalacaoVista.has(tipoId)) {
      (event.target as HTMLInputElement).checked = false;
      return;
    }
    const checked = (event.target as HTMLInputElement).checked;
    this.aceites.set(tipoId, checked);
    if (!checked) {
      this.estabelecimentos.set(tipoId, []);
    }
    this.cdr.detectChanges();
  }

  getEstabelecimentos(tipoId: number): CnesVaga[] {
    return this.estabelecimentos.get(tipoId) ?? [];
  }

  getTotalVagas(tipoId: number): number {
    return this.getEstabelecimentos(tipoId).reduce((sum, e) => sum + (e.qtdVagas || 0), 0);
  }

  hasEstabelecimentos(): boolean {
    return this.vagasAprimoramento.some((g) => this.getEstabelecimentos(g.tipoAprimoramento.id).length > 0);
  }

  nivelLabel(nivelVaga?: { id: number; nome: string }): string {
    switch (nivelVaga?.id) {
      case 1: return 'Municipal';
      case 2: return 'Estadual';
      case 4: return 'Dupla';
      default: return nivelVaga?.nome ?? '';
    }
  }

  badgeClass(nivelVaga?: { id: number; nome: string }): string {
    switch (nivelVaga?.id) {
      case 1: return 'bg-success-subtle text-success-emphasis';
      case 2: return 'bg-primary-subtle text-primary-emphasis';
      default: return 'bg-warning-subtle text-warning-emphasis';
    }
  }

  private getVagaCicloIdsSelecionados(grupo: VagaAprimoramentoGrupo): number[] {
    return this.getEstabelecimentos(grupo.tipoAprimoramento.id).flatMap((item) =>
      grupo.vagas
        .filter((v) => v.vagaPrograma?.cnes === item.cnes)
        .slice(0, item.qtdVagas)
        .map((v) => v.id),
    );
  }

  removeCnes(tipoId: number, cnes: string): void {
    const list = this.getEstabelecimentos(tipoId).filter((e) => e.cnes !== cnes);
    this.estabelecimentos.set(tipoId, list);
    this.cdr.detectChanges();
  }

  // Histórico de alterações e comprovantes do município (ou UF, gestor estadual) no ciclo (PGPP-756).
  abrirHistorico(): void {
    if (!this.cicloSelected) return;
    const ref = this.modalService.open(HistoricoConfirmacaoVagaComponent, { size: 'xl', centered: true });
    ref.componentInstance.modalRef = ref;
    ref.componentInstance.cicloSelected = this.cicloSelected;
    ref.componentInstance.escopoDescricao = this.municipio
      ? `${this.municipio.nomeMunicipio}/${this.municipio.siglaUf}`
      : this.uf
        ? `UF ${this.uf.siglaUf}`
        : null;
  }

  openInstalacaoModal(grupo: VagaAprimoramentoGrupo, event: Event): void {
    event.preventDefault();
    this.instalacaoVista.add(grupo.tipoAprimoramento.id);
    this.cdr.detectChanges();
    const ref = this.modalService.open(InstalacaoMinimaModalComponent, { size: 'lg', centered: true });
    ref.componentInstance.dsInstalacaoMinima = grupo.tipoAprimoramento.dsInstalacaoMinima;
  }

  openCnesModal(grupo: VagaAprimoramentoGrupo): void {
    const ref = this.modalService.open(CnesVagaModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'lg',
    });
    ref.componentInstance.tipoNome = grupo.tipoAprimoramento.nome;
    ref.componentInstance.vagas = grupo.vagas;
    ref.componentInstance.selecionados = this.getEstabelecimentos(grupo.tipoAprimoramento.id);
    ref.result
      .then((result: CnesVaga[]) => {
        if (result) {
          this.estabelecimentos.set(grupo.tipoAprimoramento.id, result);
          this.cdr.detectChanges();
        }
      })
      .catch(() => {});
  }

  goToStep(step: number): void {
    const prev = this.currentStep - 1;
    if (step > this.currentStep) {
      this.steps[prev].completed = true;
      this.steps[prev].active = false;
      this.steps[step - 1].active = true;
      this.steps[step - 1].disabled = false;
    } else {
      this.steps[this.currentStep - 1].active = false;
      this.steps[step - 1].active = true;
    }
    this.currentStep = step;
    this.stepChangeEmitter.emit(step);
    this.cdr.detectChanges();
  }

  goToStep2(): void {
    const temAceite = this.vagasAprimoramento.some(
      (g) => this.aceites.get(g.tipoAprimoramento.id) && this.getEstabelecimentos(g.tipoAprimoramento.id).length > 0,
    );
    if (!temAceite) {
      this.alertService.confirmation(
        'Confirmação',
        'Você não indicou nenhuma vaga neste ciclo. Deseja concluir sem aderir às vagas?',
        { label: 'Concluir', action: () => this.confirmarSemVagas() },
      );
      return;
    }
    this.goToStep(2);
  }

  goToStep3(): void {
    if (!this.cicloSelected) return;
    this.loadingService.open();
    this.termoEtapaCicloService.findByCiclo(this.cicloSelected.id, EtapaEnum.CONFIRMACAO_VAGA).subscribe({
      next: (termos) => {
        this.loadingService.close();
        this.termos = termos;
        this.goToStep(3);
      },
      error: (error) => {
        this.loadingService.close();
        this.alertService.error(error?.detail || 'Erro ao carregar termos. Tente novamente.');
      },
    });
  }

  termoConteudoSeguro(termo: any): SafeHtml {
    const conteudo = this.substituirVariaveis(termo?.modeloTermoConfirmacao?.conteudoModelo ?? '');
    return this.sanitizer.bypassSecurityTrustHtml(conteudo);
  }

  private substituirVariaveis(conteudo: string): string {
    if (!conteudo) return '';
    const variaveis = this.buildTermoVariaveis();
    return conteudo.replace(/\$\{\s*([\w.]+)\s*\}/g, (match, chave: string) =>
      Object.prototype.hasOwnProperty.call(variaveis, chave) ? variaveis[chave] : match,
    );
  }

  private buildTermoVariaveis(): Record<string, string> {

    const date = this.dataConfirmacao ?? new Date();

    return {
      municipio: this.municipio ? `${this.municipio?.siglaUf} - ${this.municipio?.nomeMunicipio ?? this.uf?.siglaUf}` : '',
      gestor: this.usuario?.nome ?? '',
      cpfGestor: this.usuario?.cpf ?? '',
      dia: date.getDate().toString().padStart(2, '0'),
      mesEscrito: date.toLocaleString('pt-BR', { month: 'long' }),
      ano: date.getFullYear().toString(),
      cnpj: this.municipio?.municipioCnpj?.cnpj || this.uf?.municipioCnpj?.cnpj || '',
    };
  }

  baixarTermoPdf(termo: any): void {
    const elemento = document.getElementById('termo-conteudo-' + termo.id);
    if (!elemento) return;

    // O bloco do termo pode estar com display:none (tela de conclusao) ou com
    // altura limitada (caixa com scroll), o que faz o html2canvas gerar PDF em
    // branco. Clonamos o conteudo para um container visivel e com layout real,
    // fora da tela, renderizamos o clone e removemos apos gerar o PDF.
    const clone = elemento.cloneNode(true) as HTMLElement;
    clone.removeAttribute('id');
    clone.style.display = 'block';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.style.width = '100%';

    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '0';
    wrapper.style.width = '800px';
    wrapper.style.background = '#ffffff';
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    const limpar = () => {
      if (wrapper.parentNode) {
        wrapper.parentNode.removeChild(wrapper);
      }
    };

    // Pagina A4 em mm: assim width/margin sao unidades fisicas reais. O
    // conteudo e renderizado a `windowWidth` px (largura do wrapper) e o jsPDF
    // deriva UMA escala unica = contentWidth / windowWidth. Sem html2canvas
    // scale fixo, que antes brigava com width/windowWidth e quebrava o layout.
    const pdf = new jsPDF('p', 'mm', 'a4');
    const margin = 10;
    const contentWidth = pdf.internal.pageSize.getWidth() - margin * 2;
    pdf.html(clone, {
      callback: (doc: jsPDF) => {
        doc.save(`${termo.modeloTermoConfirmacao?.nome || 'termo'}.pdf`);
        limpar();
      },
      margin: [margin, margin, margin, margin],
      autoPaging: 'text',
      width: contentWidth,
      windowWidth: 800,
    });
  }

  onUploadDocumento(termoId: number, anexo: any): void {
    if (anexo.file) {
      this.arquivosPorTermo.set(termoId, anexo.file);
    }
  }

  onUploadError(error: any): void {
    this.alertService.error(error?.message || 'Erro ao carregar arquivo.');
  }

  onDeleteUpload(termoId: number): void {
    this.arquivosPorTermo.delete(termoId);
  }

  baixarDocumentoAnexado(): void {
    if (!this.nuRefDocumentoConfirmacao) return;
    this.loadingService.open();
    this.uploadService.downloadArquivo(this.nuRefDocumentoConfirmacao).subscribe({
      next: async (blob: Blob) => {
        this.loadingService.close();
        // O endpoint generico de download nao retorna a extensao; detectamos
        // pelo conteudo (magic bytes) para o arquivo baixar com a extensao
        // correta. Sem assinatura conhecida (ex.: TXT), baixa sem extensao.
        const ext = await FileUtils.detectExtensionFromBlob(blob);
        const nome = `documento-assinado-${this.nuRefDocumentoConfirmacao}${ext ? '.' + ext : ''}`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nome;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.loadingService.close();
        this.alertService.error('Erro ao baixar o documento anexado.');
      },
    });
  }

  onCienciaChange(termoId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.ciencias.set(termoId, checked);
  }

  isTermoResolved(index: number): boolean {
    const termo = this.termos[index];
    if (!termo) return false;
    const uploadPendente = termo.stUploadAssinado && !this.arquivosPorTermo.has(termo.id);
    const cienciaPendente = termo.modeloTermoConfirmacao?.ciencia && !this.ciencias.get(termo.id);
    return !uploadPendente && !cienciaPendente;
  }

  isTermoInteractable(index: number): boolean {
    if (index === 0) return true;
    return this.isTermoResolved(index - 1);
  }

  async confirmar(): Promise<void> {
    if (this.acoesBloqueadas()) {
      this.alertService.error('O período de confirmação está encerrado.');
      return;
    }

    const termosComUpload = this.termos.filter((t) => t.stUploadAssinado);
    for (const termo of termosComUpload) {
      if (!this.arquivosPorTermo.has(termo.id)) {
        this.alertService.error(`Anexe o documento assinado para o termo "${termo.modeloTermoConfirmacao.nome}".`);
        return;
      }
    }

    const termosComCiencia = this.termos.filter((t) => t.modeloTermoConfirmacao?.ciencia);
    for (const termo of termosComCiencia) {
      if (!this.ciencias.get(termo.id)) {
        this.alertService.error(`Confirme a ciência do termo "${termo.modeloTermoConfirmacao.nome}".`);
        return;
      }
    }

    this.submitting = true;
    this.loadingService.open();

    try {
      let nuRefDocumento: string | undefined;

      for (const termo of termosComUpload) {
        const file = this.arquivosPorTermo.get(termo.id)!;
        const docId = await this.uploadDocumento(file);
        if (!nuRefDocumento) nuRefDocumento = docId;
      }

      const vagaCicloIds = this.vagasAprimoramento
        .filter((g) => this.aceites.get(g.tipoAprimoramento.id))
        .flatMap((g) => this.getVagaCicloIdsSelecionados(g));

      await new Promise<void>((resolve, reject) => {
        this.confirmacaoVagaService
          .saveConfirmacaoAprimoramento({
            cicloId: this.cicloSelected!.id,
            vagaCicloIds,
            nuRefDocumento: nuRefDocumento ?? null,
          })
          .subscribe({
            next: () => {
              this.steps = this.steps.map((s) => ({ ...s, completed: true, disabled: false }));
              this.confirmed = true;
              this.loadChamadaConfirmacao();
              this.cdr.detectChanges();
              resolve();
            },
            error: (error) => {
              this.alertService.error(error?.error?.detail || error?.detail || 'Erro ao confirmar vagas. Tente novamente.');
              reject();
            },
          });
      });
    } catch {
    } finally {
      this.submitting = false;
      this.loadingService.close();
    }
  }

  async confirmarSemVagas(): Promise<void> {
    if (!this.cicloSelected) return;

    this.submitting = true;
    this.loadingService.open();

    try {
      await new Promise<void>((resolve, reject) => {
        this.confirmacaoVagaService
          .saveConfirmacaoAprimoramento({
            cicloId: this.cicloSelected!.id,
            vagaCicloIds: [],
            nuRefDocumento: null,
          })
          .subscribe({
            next: () => {
              this.steps = this.steps.map((s) => ({ ...s, completed: true, disabled: false }));
              this.currentStep = 2;
              this.confirmedSemVagas = true;
              this.loadChamadaConfirmacao();
              this.cdr.detectChanges();
              resolve();
            },
            error: (error) => {
              this.alertService.error(error?.error?.detail || error?.detail || 'Erro ao concluir adesão. Tente novamente.');
              reject();
            },
          });
      });
    } catch {
    } finally {
      this.submitting = false;
      this.loadingService.close();
    }
  }

  editarAdesao(): void {
    this.confirmed = false;
    this.confirmedSemVagas = false;
    this.currentStep = 1;
    // Forca o fluxo sequencial dos termos na edicao, igual ao cadastro:
    // limpa a resolucao dos termos para que o termo anterior precise ser
    // resolvido novamente antes de interagir com o proximo.
    this.ciencias = new Map();
    this.arquivosPorTermo = new Map();
    this.steps = this.steps.map((s) => ({ ...s, active: s.number === 1, disabled: s.number > 1 ? false : s.disabled }));
    this.cdr.detectChanges();
  }

  verResumo(): void {
    this.confirmedSemVagas = false;
    // Com o periodo encerrado a visualizacao e somente leitura: nao mexe no
    // estado do wizard (steps[]) para nao habilitar passos editaveis.
    if (this.acoesBloqueadas()) {
      this.confirmed = false;
      this.currentStep = 2;
      this.cdr.detectChanges();
      return;
    }
    this.confirmed = false;
    this.goToStep(2);
  }

  // Retorna para a tela de conclusao a partir das telas somente leitura
  // (Resumo/Termos) quando o periodo esta encerrado, sem alterar steps[].
  voltarParaConclusao(): void {
    this.confirmed = true;
    this.confirmedSemVagas = false;
    this.currentStep = 3;
    this.cdr.detectChanges();
  }

  isPeriodoAberto(): boolean {
    if (!this.chamadaConfirmacao) return false;
    return new ChamadaEtapaPerfil(this.chamadaConfirmacao).stillVigente();
  }

  formatarData(date: string | number | null | undefined): string {
    if (!date) return '';
    const str = String(date);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [y, m, d] = str.split('-');
      return `${d}/${m}/${y}`;
    }
    return str;
  }

  cancelar(): void {
    this.router.navigate(['/']);
  }

  private checkConfirmacaoExistente(cicloId: number): void {
    this.loadingService.open();
    this.confirmacaoVagaService.findByCicloAndGestor(cicloId).subscribe({
      next: (confirmacao: any) => {
        if (confirmacao && confirmacao.rascunho === false) {
          const vagasConfirmadas: any[] = confirmacao.vagasCicloAprimoramento ?? [];
          if (vagasConfirmadas.length === 0) {
            this.steps = this.steps.map((s) => ({ ...s, completed: true, disabled: false }));
            this.currentStep = 2;
            this.confirmedSemVagas = true;
            this.loadVagas(cicloId);
            this.loadChamadaConfirmacao();
            this.loadingService.close();
            this.cdr.detectChanges();
            return;
          }

          this.dataConfirmacao = confirmacao.dataConfirmacao ? new Date(confirmacao.dataConfirmacao) : null;
          this.nuRefDocumentoConfirmacao = confirmacao.nuRefDocumento ?? null;
          this.termoEtapaCicloService.findByCiclo(cicloId, EtapaEnum.CONFIRMACAO_VAGA).subscribe({
            next: (termos) => {
              this.termos = termos;
              this.steps = this.steps.map((s) => ({ ...s, completed: true, disabled: false }));
              this.currentStep = 3;
              this.confirmed = true;
              this.chamadaEtapaPerfilService
                .fetchVigentePorEtapaFase(EtapaEnum.CONFIRMACAO_VAGA, FaseEnum.ADESAO_MUNICIPAL)
                .subscribe({
                  next: (chamadas) => {
                    this.loadingService.close();
                    this.chamadaConfirmacao =
                      chamadas.find((c) => c.cicloFase?.ciclo?.id === cicloId) ?? null;
                    this.chamadaCarregada = true;
                    // Os dados das vagas confirmadas sao somente leitura e devem
                    // ser exibidos no Resumo e no Termo independentemente de o
                    // periodo estar aberto ou encerrado. A vigencia so gateia
                    // acoes editaveis/submissao, nao a carga para exibicao.
                    this.loadVagas(cicloId, () => this.populateFromConfirmacao(confirmacao));
                    this.cdr.detectChanges();
                  },
                  error: () => {
                    this.loadingService.close();
                    this.chamadaCarregada = true;
                    this.cdr.detectChanges();
                  },
                });
            },
            error: () => {
              this.loadingService.close();
              this.steps = this.steps.map((s) => ({ ...s, completed: true, disabled: false }));
              this.currentStep = 3;
              this.confirmed = true;
              this.cdr.detectChanges();
            },
          });
        } else {
          this.entrarFluxoSemConfirmacao(cicloId);
        }
      },
      error: () => {
        this.entrarFluxoSemConfirmacao(cicloId);
      },
    });
  }

  private entrarFluxoSemConfirmacao(cicloId: number): void {
    this.resolveChamada(cicloId, () => {
      this.loadingService.close();
      if (this.isPeriodoAberto()) {
        this.loadVagas(cicloId);
      } else {
        // Sem confirmacao e prazo encerrado: bloqueia qualquer acao.
        this.prazoEncerrado = true;
        this.cdr.detectChanges();
      }
    });
  }

  private resolveChamada(cicloId: number, cb: () => void): void {
    this.chamadaEtapaPerfilService
      .fetchVigentePorEtapaFase(EtapaEnum.CONFIRMACAO_VAGA, FaseEnum.ADESAO_MUNICIPAL)
      .subscribe({
        next: (chamadas) => {
          this.chamadaConfirmacao =
            chamadas.find((c) => c.cicloFase?.ciclo?.id === cicloId) ?? null;
          this.chamadaCarregada = true;
          cb();
        },
        error: () => {
          this.chamadaConfirmacao = null;
          this.chamadaCarregada = true;
          cb();
        },
      });
  }

  acoesBloqueadas(): boolean {
    return !this.isPeriodoAberto();
  }

  private loadVagas(cicloId: number, onLoaded?: () => void): void {
    this.loadingService.open();
    this.confirmacaoVagaService.fetchVagasAprimoramento(cicloId).subscribe({
      next: (grupos: VagaAprimoramentoGrupo[]) => {
        this.loadingService.close();
        this.vagasAprimoramento = grupos;
        this.vagasCarregadas = true;
        grupos.forEach((g) => {
          this.aceites.set(g.tipoAprimoramento.id, false);
          this.estabelecimentos.set(g.tipoAprimoramento.id, []);
        });
        onLoaded?.();
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.loadingService.close();
        this.alertService.error(error?.detail || 'Erro ao carregar vagas. Tente novamente.');
      },
    });
  }

  private getVagaInfo(tipoId: number, cnes: string): { nomeEstabelecimento: string; nivelVaga?: { id: number; nome: string } } {
    const grupo = this.vagasAprimoramento.find((g) => g.tipoAprimoramento.id === tipoId);
    const vaga = grupo?.vagas.find((v) => v.vagaPrograma?.cnes === cnes);
    const estabelecimento = vaga?.vagaPrograma?.estabelecimento;
    return {
      nomeEstabelecimento: estabelecimento?.nomeFantasia || estabelecimento?.razaoSocial || '',
      nivelVaga: vaga?.vagaPrograma?.nivelVaga,
    };
  }

  private populateFromConfirmacao(confirmacao: any): void {
    const vagasConfirmadas: any[] = confirmacao.vagasCicloAprimoramento ?? [];
    const porTipo = new Map<
      number,
      Map<string, { nomeEstabelecimento: string; nivelVaga?: { id: number; nome: string }; count: number }>
    >();

    for (const vagaCiclo of vagasConfirmadas) {
      const tipoId: number = vagaCiclo.tipoAprimoramento?.id;
      const cnes: string = vagaCiclo.vagaPrograma?.cnes;
      if (!tipoId || !cnes) continue;

      if (!porTipo.has(tipoId)) porTipo.set(tipoId, new Map());
      const porCnes = porTipo.get(tipoId)!;
      const entry = porCnes.get(cnes);
      if (entry) {
        entry.count++;
      } else {
        porCnes.set(cnes, { ...this.getVagaInfo(tipoId, cnes), count: 1 });
      }
    }

    for (const [tipoId, porCnes] of porTipo) {
      this.instalacaoVista.add(tipoId);
      this.aceites.set(tipoId, true);
      this.estabelecimentos.set(
        tipoId,
        Array.from(porCnes.entries()).map(([cnes, { nomeEstabelecimento, nivelVaga, count }]) => ({
          cnes,
          nomeEstabelecimento,
          nivelVaga,
          qtdVagas: count,
        })),
      );
    }
  }

  private loadChamadaConfirmacao(): void {
    this.chamadaEtapaPerfilService
      .fetchVigentePorEtapaFase(EtapaEnum.CONFIRMACAO_VAGA, FaseEnum.ADESAO_MUNICIPAL)
      .subscribe({
        next: (chamadas) => {
          this.chamadaConfirmacao =
            chamadas.find((c) => c.cicloFase?.ciclo?.id === this.cicloSelected?.id) ?? null;
          this.chamadaCarregada = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.chamadaConfirmacao = null;
          this.chamadaCarregada = true;
        },
      });
  }

  private uploadDocumento(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      this.uploadService.uploadArquivo(file).subscribe({
        next: (response: any) => resolve(response?.id ?? response?.uuid ?? response),
        error: reject,
      });
    });
  }

  private resetState(): void {
    this.currentStep = 1;
    this.confirmed = false;
    this.confirmedSemVagas = false;
    this.prazoEncerrado = false;
    this.chamadaCarregada = false;
    this.chamadaConfirmacao = null;
    this.vagasAprimoramento = [];
    this.vagasCarregadas = false;
    this.termos = [];
    this.aceites = new Map();
    this.instalacaoVista = new Set();
    this.estabelecimentos = new Map();
    this.arquivosPorTermo = new Map();
    this.ciencias = new Map();
    this.nuRefDocumentoConfirmacao = null;
    this.submitting = false;
    this.steps = [
      { number: 1, title: 'Dados da Adesão', active: true, completed: false, disabled: false },
      { number: 2, title: 'Resumo', active: false, completed: false, disabled: true },
      { number: 3, title: 'Termos de Adesão', active: false, completed: false, disabled: true },
    ];
    this.cdr.detectChanges();
  }
}
